import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

// Super Admin panel. The sidebar switches the main view. Five views are wired to
// REAL endpoints (Dashboard, Doctors, Patients, Appointments, AI Alerts, Users);
// the rest are honest "in progress" stubs (no fake rows) until their backend
// lands. A demo toggle overlays pitch numbers/rows for screenshots — flip it off
// (or delete the DEMO_* blocks) to see live data.
const C = {
  blue: '#2563EB', blueSoft: '#EAF1FE', green: '#16A34A', greenSoft: '#E7F7EE',
  red: '#EF4444', redSoft: '#FEF2F2', amber: '#F59E0B', amberSoft: '#FEF6E7',
  purple: '#7C3AED', purpleSoft: '#F1EBFE', ink: '#0F172A', muted: '#64748B',
  border: '#E5E9F0', bg: '#F6F8FB', card: '#FFFFFF',
}

const DEMO = {
  totals: { patients: 12842, doctors: 256, appointments: 18742, messages: 9215, reviewAvg: 4.8 },
  deltas: { patients: 8.6, doctors: 5.3, appointments: 10.2 },
  status: { completed: 10285, upcoming: 6742, cancelled: 1715 },
  overview: [1350, 1500, 1620, 1540, 1470, 1560, 1610].map((completed, i) => ({
    date: `${12 + i} May`, upcoming: completed, completed: [600, 900, 950, 820, 880, 1000, 1080][i], cancelled: [200, 250, 300, 320, 340, 350, 360][i],
  })),
  recent: [
    { name: 'Dr. Arjun Mehta', sub: 'Dermatologist', type: 'doctor', when: 'Today, 10:30 AM' },
    { name: 'Dr. Neha Sharma', sub: 'Pediatrician', type: 'doctor', when: 'Today, 09:15 AM' },
    { name: 'Rohan Verma', sub: 'Patient', type: 'patient', when: 'Today, 08:40 AM' },
    { name: 'Priya Iyer', sub: 'Patient', type: 'patient', when: 'Yesterday' },
    { name: 'Karthik Reddy', sub: 'Patient', type: 'patient', when: 'Yesterday' },
  ],
  topSpecializations: [
    { name: 'Dermatology', count: 2845 }, { name: 'Pediatrics', count: 2310 },
    { name: 'General Physician', count: 1987 }, { name: 'Psychiatry', count: 1256 }, { name: 'Orthopedics', count: 1123 },
  ],
  growth: [4000, 5200, 6100, 7000, 8200, 9100, 10200, 11000, 11800, 12842].map((total, i) => ({ date: `${i}`, total })),
  systemHealth: { server: 'healthy', database: 'healthy', backup: 'healthy' },
}

// Demo rows for the list views (pitch screenshots only; shown when demo is ON).
const DEMO_ROWS = {
  Doctors: [
    { name: 'Dr. Arjun Mehta', specialization: 'Dermatologist', experience: '8 years', status: 'Active' },
    { name: 'Dr. Neha Sharma', specialization: 'Pediatrician', experience: '6 years', status: 'Active' },
    { name: 'Dr. Rohan Verma', specialization: 'General Physician', experience: '10 years', status: 'Active' },
    { name: 'Dr. Priya Iyer', specialization: 'Gynecologist', experience: '7 years', status: 'Active' },
    { name: 'Dr. Karthik Reddy', specialization: 'Orthopedics', experience: '9 years', status: 'Active' },
    { name: 'Dr. Sneha Joshi', specialization: 'Psychiatrist', experience: '5 years', status: 'Inactive' },
    { name: 'Dr. Ankit Patel', specialization: 'Cardiologist', experience: '12 years', status: 'Active' },
  ],
  Patients: [
    { name: 'Rohan Verma', email: 'rohan@example.com', phone: '+91 98765 43210', status: 'Active' },
    { name: 'Priya Iyer', email: 'priya@example.com', phone: '+91 91234 56789', status: 'Active' },
    { name: 'Karthik Reddy', email: 'karthik@example.com', phone: '+91 76543 21098', status: 'Active' },
    { name: 'Meera Nair', email: 'meera@example.com', phone: '+91 82341 09876', status: 'Active' },
    { name: 'Siddharth Rao', email: 'sid@example.com', phone: '+91 98760 54321', status: 'Active' },
  ],
  Appointments: [
    { name: 'Rohan Verma', doctor: 'Dr. Arjun Mehta', date: '2025-05-14', timeSlot: '10:30 AM', mode: 'Video', status: 'completed' },
    { name: 'Priya Iyer', doctor: 'Dr. Neha Sharma', date: '2025-05-14', timeSlot: '11:15 AM', mode: 'In-clinic', status: 'upcoming' },
    { name: 'Karthik Reddy', doctor: 'Dr. Rohan Verma', date: '2025-05-15', timeSlot: '09:15 AM', mode: 'Video', status: 'completed' },
    { name: 'Meera Nair', doctor: 'Dr. Priya Iyer', date: '2025-05-15', timeSlot: '02:00 PM', mode: 'In-clinic', status: 'upcoming' },
    { name: 'Siddharth Rao', doctor: 'Dr. Karthik Reddy', date: '2025-05-16', timeSlot: '10:00 AM', mode: 'Video', status: 'cancelled' },
  ],
  'AI Alerts': [
    { type: 'Low Adherence', message: 'Missed 3 doses', createdAt: '2025-05-14T09:00:00Z', read: 0 },
    { type: 'Side Effects', message: 'Reports skin rash', createdAt: '2025-05-14T08:30:00Z', read: 0 },
    { type: 'Irregular Vitals', message: 'BP higher than usual', createdAt: '2025-05-13T18:00:00Z', read: 1 },
    { type: 'Missed Follow-up', message: '2 days overdue', createdAt: '2025-05-13T10:00:00Z', read: 0 },
  ],
  'Users & Roles': [
    { name: 'Super Admin', email: 'admin@revive.com', role: 'Super Admin', status: 'Active' },
    { name: 'John Doe', email: 'john@revive.com', role: 'Admin', status: 'Active' },
    { name: 'Sarah Wilson', email: 'sarah@revive.com', role: 'Moderator', status: 'Active' },
    { name: 'Mike Brown', email: 'mike@revive.com', role: 'Support', status: 'Active' },
    { name: 'Lisa Taylor', email: 'lisa@revive.com', role: 'Content Editor', status: 'Active' },
    { name: 'David Lee', email: 'david@revive.com', role: 'Viewer', status: 'Inactive' },
  ],
  Messages: [
    { name: 'Rohan Verma', message: 'Quick question about my prescription…', when: '10:32 AM' },
    { name: 'Priya Iyer', message: 'Thank you for the follow-up!', when: 'Yesterday' },
    { name: 'Meera Nair', message: 'I am feeling better now.', when: 'Yesterday' },
  ],
  'Medication Orders': [
    { name: 'Aarav Verma', doctor: 'Dr. Arjun Mehta', medication: 'Tretinoin 0.025%', status: 'Delivered' },
    { name: 'Priya Iyer', doctor: 'Dr. Neha Sharma', medication: 'Doxycycline 100mg', status: 'Shipped' },
    { name: 'Rohan Ghost', doctor: 'Dr. Rohan Verma', medication: 'Minoxidil 5%', status: 'Processing' },
  ],
  Reports: [
    { name: 'John Doe', action: 'Revenue Report', when: '2025-05-15' },
    { name: 'Sarah Wilson', action: 'Message Report', when: '2025-05-14' },
    { name: 'Super Admin', action: 'AI Alert Report', when: '2025-05-14' },
  ],
  'Audit Logs': [
    { name: 'John Doe', action: 'Login', details: 'Successful login', when: '2025-05-18' },
    { name: 'Sarah Wilson', action: 'Updated Doctor', details: 'Dr. Priya Iyer', when: '2025-05-18' },
    { name: 'Mike Brown', action: 'Added Content', details: 'Acne Care Article', when: '2025-05-17' },
  ],
}

const fmt = n => (n == null ? '—' : Number(n).toLocaleString('en-IN'))
const cap = s => (s ? String(s)[0].toUpperCase() + String(s).slice(1) : '')
const NAV = [
  { g: 'MANAGEMENT', items: ['Dashboard', 'Doctors', 'Patients', 'Appointments', 'AI Alerts', 'Messages', 'Medication Orders'] },
  { g: 'INSIGHTS', items: ['Adherence', 'Escalations', 'Analytics', 'Reports'] },
  { g: 'CONTENT', items: ['Health Library'] },
  { g: 'SYSTEM', items: ['Users & Roles', 'Settings', 'Audit Logs'] },
]

// ── small UI atoms ───────────────────────────────────────────────────────────
const statusTone = s => {
  const v = String(s || '').toLowerCase()
  if (['active', 'completed', 'confirmed', 'delivered', 'approved', 'resolved', 'read'].includes(v)) return [C.greenSoft, C.green]
  if (['upcoming', 'shipped', 'processing', 'pending', 'open', 'in review'].includes(v)) return [C.blueSoft, C.blue]
  if (['inactive', 'cancelled', 'rejected', 'high'].includes(v)) return [C.redSoft, C.red]
  return [C.bg, C.muted]
}
function Badge({ children }) {
  const [bg, col] = statusTone(children)
  return <span style={{ fontSize: '.7rem', fontWeight: 700, padding: '.22rem .55rem', borderRadius: 999, background: bg, color: col, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{children}</span>
}
function Card({ children, style }) {
  return <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.1rem', ...(style || {}) }}>{children}</div>
}

function StatCard({ icon, bg, label, value, delta, suffix }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.1rem 1.2rem', flex: 1, minWidth: 180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>{icon}</div>
        <div style={{ fontSize: '.82rem', color: C.muted, fontWeight: 600 }}>{label}</div>
      </div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color: C.ink, lineHeight: 1 }}>{value}{suffix ? <span style={{ fontSize: '.9rem', color: C.muted, fontWeight: 600 }}> {suffix}</span> : null}</div>
      {delta != null && (
        <div style={{ fontSize: '.76rem', marginTop: 8, color: delta >= 0 ? C.green : C.red, fontWeight: 600 }}>
          {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}% <span style={{ color: C.muted, fontWeight: 400 }}>from last week</span>
        </div>
      )}
    </div>
  )
}

function LineChart({ data }) {
  const w = 460, h = 190, pad = 28
  const max = Math.max(1, ...data.flatMap(d => [d.completed, d.upcoming, d.cancelled]))
  const x = i => pad + (i * (w - pad * 2)) / Math.max(1, data.length - 1)
  const y = v => h - pad - (v / max) * (h - pad * 2)
  const line = (key, color) => <polyline fill="none" stroke={color} strokeWidth="2.5" points={data.map((d, i) => `${x(i)},${y(d[key])}`).join(' ')} />
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 190 }}>
      {[0, 0.5, 1].map(f => <line key={f} x1={pad} x2={w - pad} y1={pad + f * (h - pad * 2)} y2={pad + f * (h - pad * 2)} stroke={C.border} strokeWidth="1" />)}
      {line('upcoming', C.blue)}{line('completed', C.green)}{line('cancelled', C.red)}
      {data.map((d, i) => <text key={i} x={x(i)} y={h - 6} fontSize="9" fill={C.muted} textAnchor="middle">{String(d.date).replace(/-/g, '/').slice(5)}</text>)}
    </svg>
  )
}

function Donut({ status }) {
  const total = status.completed + status.upcoming + status.cancelled || 1
  const segs = [['completed', C.green], ['upcoming', C.blue], ['cancelled', C.red]]
  let acc = 0; const r = 60, cx = 80, cy = 80, circ = 2 * Math.PI * r
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <svg viewBox="0 0 160 160" style={{ width: 150, height: 150 }}>
        {segs.map(([k, col]) => {
          const frac = status[k] / total; const dash = frac * circ
          const el = <circle key={k} cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth="20" strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-acc * circ} transform={`rotate(-90 ${cx} ${cy})`} />
          acc += frac; return el
        })}
        <text x={cx} y={cy - 2} fontSize="20" fontWeight="800" fill={C.ink} textAnchor="middle">{fmt(total)}</text>
        <text x={cx} y={cy + 16} fontSize="10" fill={C.muted} textAnchor="middle">Total</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {segs.map(([k, col]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: col }} />
            <span style={{ color: C.muted, textTransform: 'capitalize', minWidth: 78 }}>{k}</span>
            <b style={{ color: C.ink }}>{fmt(status[k])}</b>
            <span style={{ color: C.muted, fontSize: '.72rem' }}>({Math.round((status[k] / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Area({ growth }) {
  const w = 460, h = 150, pad = 8
  const max = Math.max(1, ...growth.map(g => g.total))
  const x = i => pad + (i * (w - pad * 2)) / Math.max(1, growth.length - 1)
  const y = v => h - 12 - (v / max) * (h - 24)
  const pts = growth.map((g, i) => `${x(i)},${y(g.total)}`).join(' ')
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 150 }}>
      <polygon fill={C.blueSoft} points={`${x(0)},${h - 12} ${pts} ${x(growth.length - 1)},${h - 12}`} />
      <polyline fill="none" stroke={C.blue} strokeWidth="2.5" points={pts} />
    </svg>
  )
}

// ── generic data table view ──────────────────────────────────────────────────
function fmtWhen(v) {
  if (!v) return '—'
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) { const d = new Date(v); if (!isNaN(d)) return d.toLocaleDateString() }
  return v
}

// Slice 2: ops escalation queue — patient-reported medication issues, routed
// deterministically (doctor lane = side effect / wants to stop; ops = logistics).
function IssuesView({ fetchWithAuth }) {
  const [issues, setIssues] = React.useState(null)
  const [busy, setBusy] = React.useState(null)
  const load = React.useCallback(() => {
    fetchWithAuth(`${API_BASE}/api/admin/med-issues?status=open`).then(r => r.ok ? r.json() : { issues: [] }).then(d => setIssues(d.issues || [])).catch(() => setIssues([]))
  }, [fetchWithAuth])
  React.useEffect(() => { load() }, [load])
  const RLABEL = { forgot: 'Forgot', cost: "Can't afford / access", confused: 'Confused how to use', side_effect: 'Side effect / reaction', want_to_stop: 'Wants to stop', other: 'Other' }
  const slotIcon = s => s === 'morning' ? '🌅' : s === 'afternoon' ? '☀️' : s === 'night' ? '🌙' : ''
  const resolve = async (id) => {
    setBusy(id)
    try { await fetchWithAuth(`${API_BASE}/api/med-issue/${id}/resolve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); setIssues(prev => (prev || []).filter(i => i.id !== id)) } catch {} finally { setBusy(null) }
  }
  return (
    <div>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Escalations</h1>
      <p style={{ color: '#6b7280', fontSize: '.9rem', margin: '0 0 1.2rem' }}>Patient-reported medication issues. 🔴 Doctor = side effect / wants to stop · 🔵 Ops = logistics (forgot / cost / confused).</p>
      {issues == null ? <div style={{ color: '#6b7280', fontSize: '.9rem' }}>Loading…</div>
        : issues.length === 0 ? <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No open issues 🎉</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {issues.map(it => {
              const doctor = it.lane === 'doctor'
              return (
                <div key={it.id} style={{ background: '#fff', border: `1px solid ${doctor ? '#F5C6C6' : '#e5e7eb'}`, borderLeft: `4px solid ${doctor ? '#C0392B' : '#3B82F6'}`, borderRadius: 12, padding: '.85rem 1.1rem', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '.68rem', fontWeight: 800, padding: '3px 10px', borderRadius: 999, background: doctor ? '#FFF0F0' : '#EFF6FF', color: doctor ? '#C0392B' : '#1D4ED8', textTransform: 'uppercase', letterSpacing: '.04em' }}>{doctor ? 'Doctor' : 'Ops'}</span>
                  <div style={{ minWidth: 170 }}>
                    <div style={{ fontWeight: 700, color: '#111827', fontSize: '.9rem' }}>{it.patientName || it.patientEmail}</div>
                    <div style={{ fontSize: '.72rem', color: '#6b7280' }}>{it.patientEmail}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: '.85rem' }}>{RLABEL[it.reason] || it.reason}</div>
                    <div style={{ fontSize: '.75rem', color: '#6b7280' }}>{slotIcon(it.slot)} {it.medicine || 'medication'}{it.note ? ` — "${it.note}"` : ''}</div>
                  </div>
                  <div style={{ fontSize: '.72rem', color: '#9ca3af', minWidth: 90 }}>{fmtWhen(it.createdAt)}</div>
                  <button onClick={() => resolve(it.id)} disabled={busy === it.id} style={{ padding: '.45rem .9rem', borderRadius: 9, border: 'none', background: '#111827', color: '#fff', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}>{busy === it.id ? '…' : 'Resolve'}</button>
                </div>
              )
            })}
          </div>}
    </div>
  )
}

function ListView({ title, subtitle, columns, rows, loading, error, searchKeys, actionLabel, onAction, empty }) {
  const [q, setQ] = React.useState('')
  const filtered = React.useMemo(() => {
    if (!q.trim()) return rows
    const t = q.toLowerCase()
    return rows.filter(r => (searchKeys || Object.keys(r)).some(k => String(r[k] ?? '').toLowerCase().includes(t)))
  }, [rows, q, searchKeys])
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>{title}</h1>
          {subtitle && <p style={{ color: C.muted, margin: '.25rem 0 0', fontSize: '.85rem' }}>{subtitle}</p>}
        </div>
        {actionLabel && <button onClick={onAction} style={{ padding: '.6rem 1rem', borderRadius: 10, background: C.blue, color: '#fff', border: 'none', fontSize: '.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{actionLabel}</button>}
      </div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '.9rem 1rem', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ position: 'relative', maxWidth: 340 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`} style={{ width: '100%', padding: '.5rem .8rem .5rem 2rem', border: `1px solid ${C.border}`, borderRadius: 9, fontSize: '.82rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            <span style={{ position: 'absolute', left: '.6rem', top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: '.85rem' }}>🔍</span>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.muted, background: C.bg }}>
                {columns.map(c => <th key={c.key} style={{ padding: '.7rem 1rem', fontWeight: 600, fontSize: '.76rem', letterSpacing: '.02em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length} style={{ padding: '2rem 1rem', textAlign: 'center', color: C.muted }}>Loading…</td></tr>}
              {!loading && error && <tr><td colSpan={columns.length} style={{ padding: '2rem 1rem', textAlign: 'center', color: C.red }}>{error}</td></tr>}
              {!loading && !error && !filtered.length && <tr><td colSpan={columns.length} style={{ padding: '2.4rem 1rem', textAlign: 'center', color: C.muted }}>{empty || 'No records yet.'}</td></tr>}
              {!loading && !error && filtered.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  {columns.map(c => <td key={c.key} style={{ padding: '.7rem 1rem', color: C.ink, whiteSpace: 'nowrap' }}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && !error && filtered.length > 0 && (
          <div style={{ padding: '.7rem 1rem', borderTop: `1px solid ${C.border}`, color: C.muted, fontSize: '.78rem' }}>Showing {filtered.length} of {rows.length}</div>
        )}
      </Card>
    </div>
  )
}

function StubView({ title, note, demoRows, columns }) {
  if (demoRows && demoRows.length) return <ListView title={title} subtitle="Preview (demo data)" columns={columns} rows={demoRows} />
  return (
    <div>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 16px' }}>{title}</h1>
      <Card style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
        <div style={{ fontSize: '2.4rem', marginBottom: 10 }}>🚧</div>
        <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 6 }}>{title} — feature in progress</div>
        <div style={{ color: C.muted, fontSize: '.86rem', maxWidth: 460, margin: '0 auto' }}>{note || 'This screen has no live data yet. Flip on Demo numbers (top-right) to preview the intended layout, or check back once its backend is wired.'}</div>
      </Card>
    </div>
  )
}

// ── data hook (fetch when a view becomes active) ─────────────────────────────
function useFetch(fetchWithAuth, url, active, map) {
  const [state, setState] = React.useState({ loading: false, rows: [], error: null })
  React.useEffect(() => {
    if (!active || !url) return
    let alive = true
    setState({ loading: true, rows: [], error: null })
    fetchWithAuth(`${API_BASE}${url}`).then(async r => {
      const body = await r.json().catch(() => ({}))
      if (!alive) return
      if (!r.ok) { setState({ loading: false, rows: [], error: body.message || `Couldn't load (${r.status}).` }); return }
      setState({ loading: false, rows: map(body) || [], error: null })
    }).catch(() => alive && setState({ loading: false, rows: [], error: 'Network error.' }))
    return () => { alive = false }
  }, [fetchWithAuth, url, active, map])
  return state
}

export default function SuperAdminDashboard() {
  const { user, fetchWithAuth } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = React.useState(null)
  const [demo, setDemo] = React.useState(() => localStorage.getItem('revive_admin_demo') === '1')
  const [active, setActive] = React.useState('Dashboard')

  React.useEffect(() => {
    fetchWithAuth(`${API_BASE}/api/admin/dashboard`).then(r => r.json()).then(setData).catch(() => setData({ error: true }))
  }, [fetchWithAuth])

  // Real data for the wired list views (only fetched when their tab is open).
  const doctors = useFetch(fetchWithAuth, '/api/doctors', active === 'Doctors', React.useCallback(b => (b.doctors || []).map(d => ({ ...d, status: 'Active' })), []))
  const patients = useFetch(fetchWithAuth, '/api/patients', active === 'Patients', React.useCallback(b => (b.patients || []).map(p => ({ ...p, status: 'Active' })), []))
  const appts = useFetch(fetchWithAuth, '/api/appointments', active === 'Appointments', React.useCallback(b => b.appointments || [], []))
  const alerts = useFetch(fetchWithAuth, '/api/admin/notifications', active === 'AI Alerts', React.useCallback(b => b.notifications || [], []))
  const admins = useFetch(fetchWithAuth, '/api/admins', active === 'Users & Roles', React.useCallback(b => (b.admins || []).map(a => ({ name: a.email.split('@')[0], email: a.email, role: 'Admin', createdAt: a.createdAt, status: 'Active' })), []))
  const adherence = useFetch(fetchWithAuth, '/api/admin/adherence', active === 'Adherence', React.useCallback(b => b.patients || [], []))

  const toggleDemo = () => { const v = !demo; setDemo(v); localStorage.setItem('revive_admin_demo', v ? '1' : '0') }
  const d = demo ? DEMO : (data || {})
  const totals = d.totals || {}, deltas = d.deltas || {}, status = d.status || { completed: 0, upcoming: 0, cancelled: 0 }
  const overview = d.overview && d.overview.length ? d.overview : [{ date: '', completed: 0, upcoming: 0, cancelled: 0 }]
  const health = d.systemHealth || { server: 'healthy', database: 'healthy', backup: 'healthy' }

  // pick real rows unless demo is on (then overlay the pitch rows)
  const rowsFor = (real, key) => (demo ? (DEMO_ROWS[key] || []) : real)

  const renderMain = () => {
    switch (active) {
      case 'Doctors':
        return <ListView title="Doctors" subtitle="Manage and monitor all registered doctors." actionLabel="+ Add Doctor" onAction={() => navigate('/admin/console#staff')}
          loading={!demo && doctors.loading} error={!demo ? doctors.error : null} rows={rowsFor(doctors.rows, 'Doctors')} searchKeys={['name', 'specialization']}
          columns={[
            { key: 'name', label: 'Name', render: r => <b style={{ fontWeight: 700 }}>{r.name || '—'}</b> },
            { key: 'specialization', label: 'Specialization' },
            { key: 'experience', label: 'Experience', render: r => r.experience ? (String(r.experience).match(/year|yr/i) ? r.experience : `${r.experience} yrs`) : '—' },
            { key: 'status', label: 'Status', render: r => <Badge>{r.status}</Badge> },
          ]} />
      case 'Patients':
        return <ListView title="Patients" subtitle="Manage and monitor all registered patients." actionLabel="Export" onAction={() => navigate('/admin/console#patients')}
          loading={!demo && patients.loading} error={!demo ? patients.error : null} rows={rowsFor(patients.rows, 'Patients')} searchKeys={['name', 'email', 'phone']}
          columns={[
            { key: 'name', label: 'Name', render: r => <b style={{ fontWeight: 700 }}>{r.name || '—'}</b> },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'createdAt', label: 'Joined', render: r => fmtWhen(r.createdAt) },
            { key: 'status', label: 'Status', render: r => <Badge>{r.status}</Badge> },
          ]} />
      case 'Appointments':
        return <ListView title="Appointments" subtitle="Manage and monitor all appointments." actionLabel="Open Console" onAction={() => navigate('/admin/console#patients')}
          loading={!demo && appts.loading} error={!demo ? appts.error : null} rows={rowsFor(appts.rows, 'Appointments')} searchKeys={['name', 'doctor', 'service']}
          columns={[
            { key: 'name', label: 'Patient', render: r => <b style={{ fontWeight: 700 }}>{r.name || '—'}</b> },
            { key: 'doctor', label: 'Doctor' },
            { key: 'date', label: 'Date & Time', render: r => `${fmtWhen(r.date)}${r.timeSlot ? ' · ' + r.timeSlot : ''}` },
            { key: 'mode', label: 'Type', render: r => cap(r.mode || r.service || '—') },
            { key: 'status', label: 'Status', render: r => <Badge>{r.status}</Badge> },
          ]} />
      case 'AI Alerts':
        return <ListView title="AI Alerts" subtitle="Monitor system + clinical alerts." rows={rowsFor(alerts.rows, 'AI Alerts')} searchKeys={['type', 'message']}
          loading={!demo && alerts.loading} error={!demo ? alerts.error : null} empty="No alerts right now."
          columns={[
            { key: 'type', label: 'Type', render: r => <b style={{ fontWeight: 700 }}>{cap(r.type) || 'Notification'}</b> },
            { key: 'message', label: 'Message', render: r => <span style={{ whiteSpace: 'normal' }}>{r.message || r.body || '—'}</span> },
            { key: 'createdAt', label: 'Date', render: r => fmtWhen(r.createdAt) },
            { key: 'status', label: 'Status', render: r => <Badge>{r.read ? 'Read' : 'Open'}</Badge> },
          ]} />
      case 'Users & Roles':
        return <ListView title="Users & Roles" subtitle="Manage system users and their roles." actionLabel="+ Add User" onAction={() => navigate('/admin/console#staff')}
          loading={!demo && admins.loading} error={!demo ? admins.error : null} rows={rowsFor(admins.rows, 'Users & Roles')} searchKeys={['name', 'email', 'role']}
          columns={[
            { key: 'name', label: 'Name', render: r => <b style={{ fontWeight: 700 }}>{r.name || '—'}</b> },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Role', render: r => <Badge>{r.role}</Badge> },
            { key: 'createdAt', label: 'Joined', render: r => fmtWhen(r.createdAt) || '—' },
            { key: 'status', label: 'Status', render: r => <Badge>{r.status}</Badge> },
          ]} />
      case 'Adherence':
        return <ListView title="Medication Adherence" subtitle="Patients on active plans — worst first. Self-reported; ops chases, doctor only sees side-effect escalations."
          loading={!demo && adherence.loading} error={!demo ? adherence.error : null} rows={rowsFor(adherence.rows, 'Adherence')} searchKeys={['name', 'email', 'phone']}
          columns={[
            { key: 'name', label: 'Patient', render: r => <div><b style={{ fontWeight: 700 }}>{r.name || '—'}</b><div style={{ fontSize: '.72rem', color: '#6b7280' }}>{r.email || r.phone || ''}</div></div> },
            { key: 'perMed', label: 'By medicine · last 7 days (old → today)', render: r => <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{(r.perMed || []).map((m, i) => { const icon = m.type === 'morning' ? '🌅' : m.type === 'afternoon' ? '☀️' : '🌙'; const slot = m.type === 'morning' ? 'Morning' : m.type === 'afternoon' ? 'Afternoon' : 'Night'; return <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span title={m.name} style={{ fontSize: '.72rem', width: 190, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{icon} <b style={{ fontWeight: 600 }}>{slot}</b> · {m.name}</span><div style={{ display: 'flex', gap: 3 }}>{m.recent.map(d => { const col = d.status === 'taken' ? '#22c55e' : d.status === 'missed' ? '#C0392B' : d.status === 'pending' ? '#F59E0B' : '#e5e7eb'; return <span key={d.date} title={`${fmtWhen(d.date)}: ${d.status === 'none' ? 'no dose' : d.status === 'pending' ? 'due / not reported' : d.status}`} style={{ width: 12, height: 12, borderRadius: 3, background: col, display: 'inline-block' }} /> })}</div></div> })}</div> },
            { key: 'pct', label: 'Adherence', render: r => { const p = r.pct; const c = p == null ? { bg: '#f1f2f6', fg: '#6b7280' } : p < 50 ? { bg: '#FFF0F0', fg: '#C0392B' } : p < 80 ? { bg: '#FFF8E1', fg: '#B7770D' } : { bg: '#EBF8F0', fg: '#1A6B3C' }; return <span style={{ fontWeight: 700, fontSize: '.8rem', padding: '3px 10px', borderRadius: 999, background: c.bg, color: c.fg }}>{p == null ? '—' : p + '%'}</span> } },
            { key: 'missed', label: 'Not reported', render: r => <span style={{ fontWeight: 700, color: r.missed > 0 ? '#C0392B' : '#6b7280' }}>{r.missed}</span> },
            { key: 'pending', label: 'Pending', render: r => r.pending },
            { key: 'planEnd', label: 'Plan ends', render: r => fmtWhen(r.planEnd) },
          ]} />
      case 'Escalations':
        return <IssuesView fetchWithAuth={fetchWithAuth} />
      case 'Messages':
        return <StubView title="Messages" note="Patient ↔ clinic chat isn't wired to a backend yet. Flip on Demo numbers to preview the layout."
          demoRows={demo ? DEMO_ROWS.Messages : null} columns={[
            { key: 'name', label: 'Patient', render: r => <b style={{ fontWeight: 700 }}>{r.name}</b> },
            { key: 'message', label: 'Last message' }, { key: 'when', label: 'Time' },
          ]} />
      case 'Medication Orders':
        return <StubView title="Medication Orders" note="There's no orders/fulfilment system yet — prescriptions currently live inside clinical notes. Demo shows the intended layout."
          demoRows={demo ? DEMO_ROWS['Medication Orders'] : null} columns={[
            { key: 'name', label: 'Patient', render: r => <b style={{ fontWeight: 700 }}>{r.name}</b> },
            { key: 'doctor', label: 'Doctor' }, { key: 'medication', label: 'Medication' },
            { key: 'status', label: 'Status', render: r => <Badge>{r.status}</Badge> },
          ]} />
      case 'Reports':
        return <StubView title="Reports" note="Report generation/export isn't built yet. Demo shows sample report activity."
          demoRows={demo ? DEMO_ROWS.Reports : null} columns={[
            { key: 'name', label: 'User', render: r => <b style={{ fontWeight: 700 }}>{r.name}</b> },
            { key: 'action', label: 'Report' }, { key: 'when', label: 'Date', render: r => fmtWhen(r.when) },
          ]} />
      case 'Audit Logs':
        return <StubView title="Audit Logs" note="A platform-wide audit trail isn't wired yet (only the AI agent keeps an audit). Demo shows the intended layout."
          demoRows={demo ? DEMO_ROWS['Audit Logs'] : null} columns={[
            { key: 'name', label: 'User', render: r => <b style={{ fontWeight: 700 }}>{r.name}</b> },
            { key: 'action', label: 'Action' }, { key: 'details', label: 'Details' }, { key: 'when', label: 'Date', render: r => fmtWhen(r.when) },
          ]} />
      case 'Analytics':
        return <StubView title="Analytics" note="Deeper analytics (treatment outcomes, adherence over time) aren't aggregated yet. The Dashboard tab already shows live headline metrics." />
      case 'Health Library':
        return <StubView title="Health Library" note="Content management for articles/videos isn't wired into this panel yet." />
      case 'Settings':
        return <StubView title="Settings" note="Platform-level settings aren't stored yet. Per-clinic settings live in the doctor app. Coming to this panel next." />
      default:
        return <DashboardBody user={user} demo={demo} data={data} totals={totals} deltas={deltas} status={status} overview={overview} health={health} d={d} />
    }
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans',system-ui,sans-serif", color: C.ink }}>
      {/* Sidebar */}
      <aside style={{ width: 232, background: C.card, borderRight: `1px solid ${C.border}`, padding: '1.2rem 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 1.2rem 1.4rem', fontWeight: 800, fontSize: '1.05rem' }}>
          <span style={{ color: C.blue }}>💠</span> Revive Admin
        </div>
        {NAV.map(sec => (
          <div key={sec.g} style={{ marginBottom: 4 }}>
            <div style={{ padding: '.8rem 1.2rem .3rem', fontSize: '.66rem', letterSpacing: '.08em', color: C.muted, fontWeight: 700 }}>{sec.g}</div>
            {sec.items.map(it => (
              <button key={it} onClick={() => setActive(it)} style={{
                width: '100%', textAlign: 'left', padding: '.6rem 1.2rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '.86rem',
                background: active === it ? C.blueSoft : 'transparent', color: active === it ? C.blue : '#334155', fontWeight: active === it ? 700 : 500,
                borderLeft: active === it ? `3px solid ${C.blue}` : '3px solid transparent',
              }}>{it}</button>
            ))}
          </div>
        ))}
        <div style={{ padding: '.8rem 1.2rem .3rem', fontSize: '.66rem', letterSpacing: '.08em', color: C.muted, fontWeight: 700 }}>CLINICAL</div>
        <button onClick={() => navigate('/admin/console')} style={{ width: '100%', textAlign: 'left', padding: '.6rem 1.2rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '.86rem', background: 'transparent', color: '#334155', fontWeight: 500, borderLeft: '3px solid transparent' }}>Progress Tracker ↗</button>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '1.4rem 1.8rem', minWidth: 0 }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
            <input placeholder="Search anything…" style={{ width: '100%', padding: '.6rem .9rem .6rem 2.2rem', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: '.85rem', outline: 'none', fontFamily: 'inherit', background: C.card, boxSizing: 'border-box' }} />
            <span style={{ position: 'absolute', left: '.75rem', top: '50%', transform: 'translateY(-50%)', color: C.muted }}>🔍</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={toggleDemo} title="Toggle pitch/demo numbers" style={{ padding: '.45rem .8rem', borderRadius: 8, border: `1px solid ${demo ? C.amber : C.border}`, background: demo ? C.amberSoft : C.card, color: demo ? '#92680C' : C.muted, fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {demo ? '● DEMO NUMBERS' : '○ Real data'}
            </button>
            <span style={{ fontSize: '1.1rem' }}>🔔</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg,${C.blue},#60A5FA)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>{(user?.name || 'A')[0].toUpperCase()}</div>
              <div style={{ lineHeight: 1.1 }}><div style={{ fontSize: '.82rem', fontWeight: 700 }}>{user?.name || 'Admin'}</div><div style={{ fontSize: '.7rem', color: C.muted }}>Super Admin</div></div>
            </div>
          </div>
        </div>

        {renderMain()}
      </main>
    </div>
  )
}

// Dashboard tab body (the overview screen) — kept as its own component.
function DashboardBody({ user, demo, data, totals, deltas, status, overview, health, d }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Dashboard</h1>
          <p style={{ color: C.muted, margin: '.25rem 0 0', fontSize: '.85rem' }}>Welcome back, {user?.name || 'Admin'}! Here's what's happening today.</p>
        </div>
        <button style={{ padding: '.6rem 1rem', borderRadius: 10, background: C.blue, color: '#fff', border: 'none', fontSize: '.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⬇ Export Report</button>
      </div>

      {!demo && data && data.error && <div style={{ background: C.redSoft, border: `1px solid ${C.red}`, color: '#991B1B', borderRadius: 10, padding: '.7rem 1rem', marginBottom: 16, fontSize: '.82rem' }}>Couldn't load live stats.</div>}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <StatCard icon="👥" bg={C.blueSoft} label="Total Patients" value={fmt(totals.patients)} delta={deltas.patients} />
        <StatCard icon="🩺" bg={C.greenSoft} label="Total Doctors" value={fmt(totals.doctors)} delta={deltas.doctors} />
        <StatCard icon="📅" bg={C.purpleSoft} label="Total Appointments" value={fmt(totals.appointments)} delta={deltas.appointments} />
        <StatCard icon="💬" bg={C.amberSoft} label="Messages Sent" value={fmt(totals.messages)} />
        <StatCard icon="⭐" bg={C.redSoft} label="Patient Reviews" value={totals.reviewAvg != null ? totals.reviewAvg : '—'} suffix="out of 5" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.1fr 1fr', gap: 14, marginBottom: 18 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Appointments Overview</div>
          <div style={{ display: 'flex', gap: 14, fontSize: '.72rem', color: C.muted, marginBottom: 4 }}>
            <span>● <span style={{ color: C.green }}>Completed</span></span><span>● <span style={{ color: C.blue }}>Upcoming</span></span><span>● <span style={{ color: C.red }}>Cancelled</span></span>
          </div>
          <LineChart data={overview} />
        </Card>
        <Card><div style={{ fontWeight: 700, marginBottom: 14 }}>Appointments by Status</div><Donut status={status} /></Card>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Recent Registrations</div>
          {(d.recent || []).slice(0, 5).map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '.5rem 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: r.type === 'doctor' ? C.greenSoft : C.blueSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem' }}>{r.type === 'doctor' ? '🩺' : '🧑'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.82rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                <div style={{ fontSize: '.72rem', color: C.muted }}>{r.sub}</div>
              </div>
              <div style={{ fontSize: '.68rem', color: C.muted, whiteSpace: 'nowrap' }}>{typeof r.when === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(r.when) ? new Date(r.when).toLocaleDateString() : (r.when || '')}</div>
            </div>
          ))}
          {(!d.recent || !d.recent.length) && <div style={{ color: C.muted, fontSize: '.8rem', padding: '.5rem 0' }}>No registrations yet.</div>}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr', gap: 14 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Top Specializations</div>
          {(d.topSpecializations || []).map((s, i) => {
            const max = Math.max(1, ...(d.topSpecializations || []).map(x => x.count))
            return (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', marginBottom: 4 }}><span>{s.name}</span><b>{fmt(s.count)}</b></div>
                <div style={{ height: 6, background: C.bg, borderRadius: 3 }}><div style={{ width: `${(s.count / max) * 100}%`, height: 6, background: C.blue, borderRadius: 3 }} /></div>
              </div>
            )
          })}
          {(!d.topSpecializations || !d.topSpecializations.length) && <div style={{ color: C.muted, fontSize: '.8rem' }}>No data yet.</div>}
        </Card>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontWeight: 700 }}>Patient Growth</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmt(totals.patients)}</div>
          </div>
          <Area growth={d.growth && d.growth.length ? d.growth : [{ total: 0 }, { total: 0 }]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>System Health</div>
          {[['Server Status', health.server, 'All systems operational'], ['Database', health.database, 'Performance optimal'], ['Backup', health.backup, 'Automatic']].map(([label, st, sub], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: '.82rem', fontWeight: 600 }}>{label}</div><div style={{ fontSize: '.7rem', color: C.muted }}>{sub}</div></div>
              <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '.2rem .5rem', borderRadius: 6, background: st === 'healthy' ? C.greenSoft : C.redSoft, color: st === 'healthy' ? C.green : C.red }}>{st === 'healthy' ? 'Healthy' : 'Down'}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  )
}
